// Order Reports — one row per invoice line for every CLOSED order in
// the window (iter-257 consolidation: replaces the old flat orders
// list). Packages explode into a header row + one row per contained
// service, priced pro-rata so the SUM of the children equals the
// package master's final billed amount, exactly like the Order page.
//
// Strict closure rule: only `status == "completed"` orders show up.
// Drafts, open carts, partials, no-shows, voided invoices and
// standalone deposits are excluded server-side.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import ReportShell from "./ReportShell";
import DateRangeFilter, { useDateRange } from "./DateRangeFilter";

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(`${d}T00:00:00+08:00`).toLocaleDateString("en-GB", {
      timeZone: "Asia/Makassar", day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return d; }
}

// Tone hooks for the three row varieties. Package headers stand out
// with a subtle amber wash, children sit indented with a muted tone —
// no glyphs on the item name column (iter-257 typography cleanup:
// plain text only).
const ROW_TONE = {
  package_header: "bg-amber-50/40",
  package_child: "bg-muted/10",
  order_line: "",
};

export default function OrdersReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range } = useDateRange("last_week");

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/reports/branch/${bid}/orders-granular`, { params: range });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  const exportSpec = useMemo(() => {
    if (!data) return null;
    // Iter-318 · Excel emits raw numbers on every money column; PDF
    // keeps the branch-currency label via the column's `format`.
    const money = (v) => formatBranchMoney(branch, v || 0);
    const num = { numeric: true, format: money, align: "right" };
    const columns = [
      { header: "Date", key: "date" },
      { header: "Order Number", key: "order_number" },
      { header: "Customer First Name", key: "customer_first" },
      { header: "Customer Last Name", key: "customer_last" },
      { header: "Item Name", key: "item_name" },
      { header: "Item Type", key: "item_type" },
      { header: "Item Category", key: "item_category" },
      { header: "Care Team", key: "specialist_name" },
      { header: "Qty", key: "quantity", align: "right" },
      { header: "Original Price", key: "original_price", ...num },
      { header: "Adjustment", key: "adjustment", ...num },
      { header: "Sub Total", key: "sub_total", ...num },
      { header: "Service Charge", key: "service_charge", ...num },
      { header: "Tax", key: "tax", ...num },
      { header: "Total", key: "total", ...num },
      { header: "Payment Type", key: "payment_types" },
      { header: "Order Closed By", key: "closed_by_name" },
    ];

    const rows = (data.rows || []).map((r) => ({
      date: fmtDate(r.date),
      order_number: r.order_number ? `#${r.order_number}` : "",
      customer_first: r.customer_first || "",
      customer_last: r.customer_last || "",
      // Plain text — no arrows / emoji prefixes for packages or
      // children so the Excel export reads as a clean list.
      item_name: r.item_name,
      item_type: r.item_type || "",
      item_category: r.item_category || "",
      specialist_name: r.specialist_name || "",
      quantity: r.quantity,
      original_price: Number(r.original_price) || 0,
      adjustment: Number(r.adjustment) || 0,
      sub_total: Number(r.sub_total) || 0,
      service_charge: Number(r.service_charge) || 0,
      tax: Number(r.tax) || 0,
      total: Number(r.total) || 0,
      payment_types: r.payment_types || "",
      closed_by_name: r.closed_by_name || "",
    }));

    const footer = [
      `Totals · ${data.order_count} order${data.order_count === 1 ? "" : "s"} · ${data.count} row${data.count === 1 ? "" : "s"}`,
      "", "", "", "", "", "", "",
      "",
      Number(data.totals?.original_price) || 0,
      Number(data.totals?.adjustment) || 0,
      Number(data.totals?.sub_total) || 0,
      Number(data.totals?.service_charge) || 0,
      Number(data.totals?.tax) || 0,
      Number(data.totals?.total) || 0,
      "", "",
    ];

    return {
      filename: "order-reports",
      range: { date_from: data.date_from, date_to: data.date_to },
      columns,
      rows,
      footerRow: footer,
    };
  }, [data, branch]);

  return (
    <ReportShell
      title="Order Reports"
      subtitle="Every line on every closed invoice · packages exploded pro-rata into their services."
      branchName={branch?.name}
      tab="sales"
      exportSpec={exportSpec}
    >
      <div className="space-y-4">
        <DateRangeFilter
          preset={preset} setPreset={setPreset}
          customFrom={customFrom} setCustomFrom={setCustomFrom}
          customTo={customTo} setCustomTo={setCustomTo}
          loading={loading} onRefresh={load}
        />

        <Card className="rounded-lg overflow-hidden" data-testid="orders-report-card">
          <CardContent className="p-0 overflow-x-auto">
            <div className="p-4 border-b border-border flex items-baseline justify-between">
              <div>
                <p className="tracking-overline text-[10px] text-muted-foreground">Report</p>
                <h2 className="text-lg font-semibold">Orders</h2>
                {branch?.name && (
                  <p className="text-xs text-muted-foreground mt-0.5" data-testid="orders-report-location">
                    Cocoon Medical Spa, {branch.name}
                  </p>
                )}
              </div>
              {data && (
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {data.order_count} closed order{data.order_count === 1 ? "" : "s"} · {data.count} row{data.count === 1 ? "" : "s"} · grand total
                  </div>
                  <div className="text-xl font-semibold" data-testid="orders-report-total">
                    {formatBranchMoney(branch, data.totals?.total || 0)}
                  </div>
                </div>
              )}
            </div>

            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="uppercase text-[10px] tracking-wider">Date</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Order #</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">First Name</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Last Name</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider min-w-[200px]">Item Name</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Item Type</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Item Category</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider" data-testid="orders-report-hdr-care-team">Care Team</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Qty</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Original Price</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Adjustment</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Sub Total</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Service Charge</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Tax</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Total</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Payment Type</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Order Closed By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!data || data.count === 0) ? (
                  <TableRow>
                    <TableCell colSpan={17} className="text-center text-sm text-muted-foreground py-8">
                      {loading ? "Loading…" : "No closed orders in this range."}
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {data.rows.map((r, i) => {
                      const key = `${r.order_id}-${i}`;
                      const tone = ROW_TONE[r.row_type] || "";
                      const isChild = r.row_type === "package_child";
                      const isPkgHeader = r.row_type === "package_header";
                      return (
                        <TableRow
                          key={key}
                          className={tone}
                          data-testid={`orders-report-row-${i}`}
                          data-row-type={r.row_type}
                        >
                          <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                          <TableCell className="text-sm font-medium">
                            <Link
                              to={`/staff/branch/${bid}/orders?order=${r.order_id}`}
                              className="text-primary hover:underline"
                              data-testid={`orders-report-open-${i}`}
                            >
                              #{r.order_number}
                            </Link>
                          </TableCell>
                          <TableCell className="text-sm">{r.customer_first}</TableCell>
                          <TableCell className="text-sm">{r.customer_last}</TableCell>
                          {/* Plain text item name — no glyphs. Package
                              headers stand out via the amber tone on
                              the whole row instead. */}
                          <TableCell className={"text-sm " + (isPkgHeader ? "font-semibold text-amber-900" : "") + (isChild ? " pl-6 text-muted-foreground" : "")}>
                            {r.item_name}
                          </TableCell>
                          <TableCell className="text-xs">{r.item_type}</TableCell>
                          <TableCell className="text-xs">{r.item_category}</TableCell>
                          <TableCell className="text-xs">{r.specialist_name || <span className="text-muted-foreground italic">—</span>}</TableCell>
                          <TableCell className="text-right text-sm">{r.quantity}</TableCell>
                          <TableCell className="text-right text-sm">{formatBranchMoney(branch, r.original_price)}</TableCell>
                          <TableCell className={"text-right text-sm " + (r.adjustment < 0 ? "text-rose-700" : "")}>
                            {r.adjustment ? formatBranchMoney(branch, r.adjustment) : formatBranchMoney(branch, 0)}
                          </TableCell>
                          <TableCell className="text-right text-sm">{formatBranchMoney(branch, r.sub_total)}</TableCell>
                          <TableCell className="text-right text-sm">{formatBranchMoney(branch, r.service_charge)}</TableCell>
                          <TableCell className="text-right text-sm">{formatBranchMoney(branch, r.tax)}</TableCell>
                          <TableCell className={"text-right " + (isPkgHeader ? "" : "font-semibold")}>
                            {formatBranchMoney(branch, r.total)}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap">{r.payment_types}</TableCell>
                          <TableCell className="text-xs whitespace-nowrap">{r.closed_by_name}</TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow className="bg-muted/40 font-semibold" data-testid="orders-report-totals-row">
                      <TableCell className="text-xs" colSpan={9}>
                        Totals · {data.order_count} order{data.order_count === 1 ? "" : "s"} · {data.count} row{data.count === 1 ? "" : "s"}
                      </TableCell>
                      <TableCell className="text-right text-sm">{formatBranchMoney(branch, data.totals?.original_price || 0)}</TableCell>
                      <TableCell className="text-right text-sm">{formatBranchMoney(branch, data.totals?.adjustment || 0)}</TableCell>
                      <TableCell className="text-right text-sm">{formatBranchMoney(branch, data.totals?.sub_total || 0)}</TableCell>
                      <TableCell className="text-right text-sm">{formatBranchMoney(branch, data.totals?.service_charge || 0)}</TableCell>
                      <TableCell className="text-right text-sm">{formatBranchMoney(branch, data.totals?.tax || 0)}</TableCell>
                      <TableCell className="text-right">{formatBranchMoney(branch, data.totals?.total || 0)}</TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </ReportShell>
  );
}
